// Agent rooms (M4-05; 06 §Channels "Agent rooms"; 04 §Spaces and chat take-over/release; 05
// §channel `agent_room`, §Personal crews "Sharing policy"; ADR-0010). A room is a channel or a
// topic with `is_agent_room` (M2-02's shell: the flag and `channels|topics/{id}/take-over|release`,
// which now also park and resume the room's runs). These routes add the participants (agents
// post in turn; people join), the turn and its last-N message window, and the status asks agents
// put to each other under the target's sharing policy. All of it is the `agents` add-on: people
// and agents call the same routes (`member`: a session, an agent key or a run's token), each
// narrowed to the room's channel as chat narrows it (a crew to what its owner can see).
import { z } from "zod";
import { AgentRoom, AgentRoomParticipant, StatusAsk, StatusAskAnswer } from "../agent-room-rows";
import { MembershipKind, RunState, StatusAskState, StatusAskSubjectKind } from "../enums";
import { NonEmpty, Timestamp, text, Uuid } from "../primitives";
import { Message } from "../work-rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

// Views -----------------------------------------------------------------------------------------

/**
 * What a participant's messages carry (06; research rec. 1): `crew` "Maya's agent", `de` the DE
 * badge and its seat, `person` the name.
 */
export const AgentRoomBadge = z
  .object({
    kind: z.enum(["person", "de", "crew"]),
    label: z.string(),
    seat_title: z.string().nullable(),
    owner_membership_id: Uuid.nullable(),
  })
  .meta({ id: "AgentRoomBadge" });

export const AgentRoomParticipantView = AgentRoomParticipant.extend({
  display_name: z.string(),
  member_kind: MembershipKind,
  badge: AgentRoomBadge,
  is_turn: z.boolean(),
  /** The agent's latest live run in the room (`parked` while a take-over holds it). */
  run: z.object({ id: Uuid, state: RunState, parked: z.boolean() }).nullable(),
}).meta({ id: "AgentRoomParticipantView" });

/** A room as its header renders it: the room, its place, the take-over, people and agents. */
export const AgentRoomView = z
  .object({
    room: AgentRoom,
    name: z.string(),
    space_id: Uuid.nullable(),
    taken_over_by_membership_id: Uuid.nullable(),
    taken_over_at: Timestamp.nullable(),
    participants: z.array(AgentRoomParticipantView),
    parked_runs: z.int().min(0),
    open_status_asks: z.int().min(0),
  })
  .meta({ id: "AgentRoomView" });

/**
 * One of three: a new room channel in a space (`space_id` + `name`), or an existing channel or
 * topic flagged a room (`channel_id` | `topic_id`). Agents and people named in
 * `participant_ids` join it (a crew only by its owner).
 */
export const AgentRoomCreate = z
  .strictObject({
    space_id: Uuid.optional(),
    name: NonEmpty.optional(),
    is_private: z.boolean().optional(),
    channel_id: Uuid.optional(),
    topic_id: Uuid.optional(),
    participant_ids: z.array(Uuid).max(20).optional(),
    window_size: z.int().min(1).max(200).optional(),
    max_agent_turns: z.int().min(1).max(50).optional(),
  })
  .refine(
    (b) =>
      (b.space_id === undefined) === (b.name === undefined) &&
      Number(b.space_id !== undefined) +
        Number(b.channel_id !== undefined) +
        Number(b.topic_id !== undefined) ===
        1,
    { message: "exactly one of space_id + name, channel_id and topic_id" },
  )
  .meta({ id: "AgentRoomCreate" });

export const AgentRoomPatch = z
  .strictObject({
    window_size: z.int().min(1).max(200).optional(),
    max_agent_turns: z.int().min(1).max(50).optional(),
  })
  .refine((p) => Object.keys(p).length > 0, { message: "at least one field" })
  .meta({ id: "AgentRoomPatch" });

export const AgentRoomListQuery = PageQuery.extend({ channel_id: Uuid.optional() });

export const AgentRoomParticipantAdd = z
  .strictObject({ membership_id: Uuid })
  .meta({ id: "AgentRoomParticipantAdd" });
export const AgentRoomParticipantParams = z.object({ co: Uuid, id: Uuid, membership_id: Uuid });

/**
 * `GET agent-rooms/{id}/turn`: whose turn it is and the last-N messages of the room (oldest
 * first) — what an agent reads on its turn (research rec. 20) — with the thread to reply in.
 */
export const AgentRoomTurn = z
  .object({
    room_id: Uuid,
    membership_id: Uuid.nullable(),
    seq: z.int().min(0),
    started_at: Timestamp.nullable(),
    stalled: z.boolean(),
    agent_turns_since_human: z.int().min(0),
    is_mine: z.boolean(),
    taken_over: z.boolean(),
    reply_thread_id: Uuid.nullable(),
    window_size: z.int().min(1).max(200),
    window: z.array(Message),
  })
  .meta({ id: "AgentRoomTurn" });

export const StatusAskCreate = z
  .strictObject({
    target_membership_id: Uuid,
    subject_kind: StatusAskSubjectKind,
    subject_id: Uuid,
    question: text(500).optional(),
  })
  .meta({ id: "StatusAskCreate" });
export const StatusAskAnswerBody = StatusAskAnswer.extend({
  note: StatusAskAnswer.shape.note.optional(),
}).meta({ id: "StatusAskAnswerBody" });
export const StatusAskDecline = z
  .strictObject({ reason: text(500).optional() })
  .meta({ id: "StatusAskDecline" });
export const StatusAskListQuery = PageQuery.extend({ state: StatusAskState.optional() });

// Routes ----------------------------------------------------------------------------------------

export const agentRoomRoutes = {
  "agentRooms.list": defineRoute({
    ...agents,
    id: "agentRooms.list",
    method: "get",
    path: `${base}/agent-rooms`,
    tag: "agentRooms",
    summary: "The agent rooms the caller can view (optionally of one channel)",
    access: "member",
    params: CompanyParams,
    query: AgentRoomListQuery,
    response: { status: 200, schema: page(AgentRoomView) },
    errors: [],
    emits: [],
  }),
  "agentRooms.create": defineRoute({
    ...agents,
    id: "agentRooms.create",
    method: "post",
    path: `${base}/agent-rooms`,
    tag: "agentRooms",
    summary: "Make an agent room: a new room channel, or an existing channel or topic flagged one",
    access: "member",
    params: CompanyParams,
    body: AgentRoomCreate,
    idempotency: "optional",
    response: { status: 201, schema: AgentRoomView },
    errors: ["conflict"],
    emits: [
      "agent_room.created",
      "agent_room.participant_changed",
      "agent_room.turn_changed",
      "channel.created",
      "channel.updated",
      "channel.member_changed",
      "topic.created",
    ],
  }),
  "agentRooms.get": defineRoute({
    ...agents,
    id: "agentRooms.get",
    method: "get",
    path: `${base}/agent-rooms/{id}`,
    tag: "agentRooms",
    summary: "An agent room with its participants, badges, turn and take-over",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentRoomView },
    errors: [],
    emits: [],
  }),
  "agentRooms.update": defineRoute({
    ...agents,
    id: "agentRooms.update",
    method: "patch",
    path: `${base}/agent-rooms/{id}`,
    tag: "agentRooms",
    summary: "Set the room's turn window or its agent-turn limit (the room's managers)",
    access: "member",
    params: CompanyItemParams,
    body: AgentRoomPatch,
    response: { status: 200, schema: AgentRoomView },
    errors: [],
    emits: ["agent_room.updated"],
  }),
  // ACTIONS: close a room (its managers). Refused (409) while it is taken over, a run of it is
  // parked, or it has status asks (they are the room's record). The messages stay: a room
  // channel of its own is archived, a channel or topic flagged a room becomes a plain one.
  "agentRooms.delete": defineRoute({
    ...agents,
    id: "agentRooms.delete",
    method: "delete",
    path: `${base}/agent-rooms/{id}`,
    tag: "agentRooms",
    summary: "Close a room (its managers); its channel and messages are kept",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["agent_room.removed", "channel.archived", "channel.updated"],
  }),
  "agentRooms.addParticipant": defineRoute({
    ...agents,
    id: "agentRooms.addParticipant",
    method: "post",
    path: `${base}/agent-rooms/{id}/participants`,
    tag: "agentRooms",
    summary: "Add an agent (a DE, or the caller's own crew) or a person to the room",
    access: "member",
    params: CompanyItemParams,
    body: AgentRoomParticipantAdd,
    idempotency: "optional",
    response: { status: 200, schema: AgentRoomView },
    errors: ["conflict"],
    emits: ["agent_room.participant_changed", "agent_room.turn_changed", "channel.member_changed"],
  }),
  "agentRooms.removeParticipant": defineRoute({
    ...agents,
    id: "agentRooms.removeParticipant",
    method: "delete",
    path: `${base}/agent-rooms/{id}/participants/{membership_id}`,
    tag: "agentRooms",
    summary: "Remove a participant (the room's managers, a crew's owner, or oneself)",
    access: "member",
    params: AgentRoomParticipantParams,
    response: { status: 200, schema: AgentRoomView },
    errors: [],
    emits: [
      "agent_room.participant_changed",
      "agent_room.turn_changed",
      "channel.member_changed",
      "wakeup.requested",
    ],
  }),
  "agentRooms.join": defineRoute({
    ...agents,
    id: "agentRooms.join",
    method: "post",
    path: `${base}/agent-rooms/{id}/join`,
    tag: "agentRooms",
    summary: "Join the room as a person (06 Join; a no-op when already in it)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: AgentRoomView },
    errors: [],
    emits: ["agent_room.participant_changed"],
  }),
  "agentRooms.leave": defineRoute({
    ...agents,
    id: "agentRooms.leave",
    method: "post",
    path: `${base}/agent-rooms/{id}/leave`,
    tag: "agentRooms",
    summary: "Leave the room as a person",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: AgentRoomView },
    errors: ["conflict"],
    emits: ["agent_room.participant_changed"],
  }),
  "agentRooms.turn": defineRoute({
    ...agents,
    id: "agentRooms.turn",
    method: "get",
    path: `${base}/agent-rooms/{id}/turn`,
    tag: "agentRooms",
    summary: "Whose turn it is, with the room's last-N messages (the turn's window)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentRoomTurn },
    errors: [],
    emits: [],
  }),
  "agentRooms.pass": defineRoute({
    ...agents,
    id: "agentRooms.pass",
    method: "post",
    path: `${base}/agent-rooms/{id}/pass`,
    tag: "agentRooms",
    summary: "The agent whose turn it is hands the turn on without posting",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: AgentRoomTurn },
    errors: ["conflict"],
    emits: ["agent_room.turn_changed", "wakeup.requested"],
  }),
  "statusAsks.list": defineRoute({
    ...agents,
    id: "statusAsks.list",
    method: "get",
    path: `${base}/agent-rooms/{id}/status-asks`,
    tag: "agentRooms",
    summary: "The room's status asks, newest first",
    access: "member",
    params: CompanyItemParams,
    query: StatusAskListQuery,
    response: { status: 200, schema: page(StatusAsk) },
    errors: [],
    emits: [],
  }),
  "statusAsks.create": defineRoute({
    ...agents,
    id: "statusAsks.create",
    method: "post",
    path: `${base}/agent-rooms/{id}/status-asks`,
    tag: "agentRooms",
    summary:
      "An agent asks another agent in the room for a task's status (decided by the target's sharing policy; refused → its human)",
    access: "member",
    params: CompanyItemParams,
    body: StatusAskCreate,
    idempotency: "required",
    response: { status: 201, schema: StatusAsk },
    errors: ["conflict"],
    emits: ["status_ask.created", "status_ask.escalated", "message.created", "wakeup.requested"],
  }),
  "statusAsks.answer": defineRoute({
    ...agents,
    id: "statusAsks.answer",
    method: "post",
    path: `${base}/status-asks/{id}/answer`,
    tag: "agentRooms",
    summary: "Answer a status ask (its target agent, or the human it escalated to)",
    access: "member",
    params: CompanyItemParams,
    body: StatusAskAnswerBody,
    idempotency: "optional",
    response: { status: 200, schema: StatusAsk },
    errors: ["conflict"],
    emits: ["status_ask.answered", "message.created", "wakeup.requested"],
  }),
  "statusAsks.decline": defineRoute({
    ...agents,
    id: "statusAsks.decline",
    method: "post",
    path: `${base}/status-asks/{id}/decline`,
    tag: "agentRooms",
    summary: "Decline an escalated status ask (the human it escalated to)",
    access: "member",
    params: CompanyItemParams,
    body: StatusAskDecline,
    idempotency: "optional",
    response: { status: 200, schema: StatusAsk },
    errors: ["conflict"],
    emits: ["status_ask.declined", "message.created", "wakeup.requested"],
  }),
} as const;
